import { todayLocal } from '@/lib/dates'
import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useEmployeeOptions } from '@/features/employee'
import { WEEK_DAYS, shortDayName, type ShiftView } from '../types'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

export interface AssignRosterValues {
  employeeIds: string[]
  shiftId: string
  fromDate: string
  toDate: string | null
  weekOffDays: string[]
}

function today(): string {
  return todayLocal()
}

/**
 * Puts people on a shift.
 *
 * <p>Assignment is a bulk action because that is how rosters are actually changed — a
 * whole team moves to nights, not one person at a time — and because each save closes
 * off the assignment it replaces, so doing it one by one would be needlessly slow.
 */
export function AssignRosterForm({
  shifts,
  onSubmit,
  onCancel,
  submitting,
}: {
  shifts: ShiftView[]
  onSubmit: (values: AssignRosterValues) => void
  onCancel: () => void
  submitting: boolean
}) {
  const employees = useEmployeeOptions()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [shiftId, setShiftId] = useState(shifts[0]?.id ?? '')
  const [fromDate, setFromDate] = useState(today())
  const [toDate, setToDate] = useState('')
  const [weekOffs, setWeekOffs] = useState<string[]>(['SATURDAY', 'SUNDAY'])

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const all = employees.data ?? []
    if (!needle) return all
    return all.filter(
      (employee) =>
        employee.displayName.toLowerCase().includes(needle) ||
        employee.employeeCode.toLowerCase().includes(needle) ||
        (employee.departmentName ?? '').toLowerCase().includes(needle),
    )
  }, [employees.data, search])

  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )
  }

  function toggleDay(day: string) {
    setWeekOffs((current) =>
      current.includes(day) ? current.filter((value) => value !== day) : [...current, day],
    )
  }

  const canSubmit = selected.length > 0 && shiftId !== '' && !submitting

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit({
          employeeIds: selected,
          shiftId,
          fromDate,
          toDate: toDate === '' ? null : toDate,
          weekOffDays: weekOffs,
        })
      }}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="roster-shift">Shift</Label>
          <Select
            id="roster-shift"
            required
            value={shiftId}
            onChange={(event) => setShiftId(event.target.value)}
            className="w-full"
          >
            {shifts.map((shift) => (
              <option key={shift.id} value={shift.id}>
                {shift.name} ({shift.startTime.slice(0, 5)}–{shift.endTime.slice(0, 5)})
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="roster-from">Effective from</Label>
          <DatePicker
            id="roster-from"
            required
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="roster-to">Until</Label>
          <DatePicker
            id="roster-to"
            min={fromDate}
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted)]">Leave blank to keep it open-ended.</p>
        </div>
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-[var(--color-heading)]">Weekly off days</legend>
        <div className="flex flex-wrap gap-2">
          {WEEK_DAYS.map((day) => (
            <button
              key={day}
              type="button"
              aria-pressed={weekOffs.includes(day)}
              onClick={() => toggleDay(day)}
              className={cn(
                'rounded-[var(--radius-control)] border px-3 py-1.5 text-sm transition-colors',
                weekOffs.includes(day)
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] text-[var(--color-body)] hover:border-[var(--color-primary)]',
              )}
            >
              {shortDayName(day)}
            </button>
          ))}
        </div>
      </fieldset>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="roster-search">Employees</Label>
          <span className="text-xs text-[var(--color-muted)]">
            {selected.length} selected
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => setSelected([])}
                className="ms-2 text-[var(--color-primary)] underline"
              >
                Clear
              </button>
            )}
          </span>
        </div>
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
            aria-hidden="true"
          />
          <Input
            id="roster-search"
            className="ps-9"
            placeholder="Search by name, code or department"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="mt-2 max-h-64 overflow-y-auto overscroll-contain rounded-[var(--radius-control)] border border-[var(--color-border)]">
          {employees.isLoading && <p className="p-3 text-sm text-[var(--color-muted)]">Loading directory…</p>}
          {!employees.isLoading && matches.length === 0 && (
            <p className="p-3 text-sm text-[var(--color-muted)]">Nobody matches “{search}”.</p>
          )}
          <ul>
            {matches.map((employee) => (
              <li key={employee.id}>
                <label className="flex cursor-pointer items-center gap-3 border-b border-[var(--color-border)] px-3 py-2 text-sm last:border-b-0 hover:bg-[var(--color-surface)]">
                  <input
                    type="checkbox"
                    checked={selected.includes(employee.id)}
                    onChange={() => toggle(employee.id)}
                    className="size-4 accent-[var(--color-primary)]"
                  />
                  <span className="min-w-0 flex-1 truncate text-[var(--color-heading)]">
                    {employee.displayName}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-[var(--color-muted)]">
                    {employee.employeeCode}
                  </span>
                  <span className="hidden shrink-0 text-xs text-[var(--color-muted)] sm:inline">
                    {employee.departmentName ?? '—'}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? 'Assigning…' : `Assign ${selected.length || ''}`.trim()}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
